import React, { useEffect } from "react";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, HoverCard } from "@mantine/core";

import { AddYourCodeUpsell, CompactSingleSelect } from "@components";
import { useTimeLookbackRange } from "@hooks";
import {
  TIME_LOOKBACK_RANGES,
  TIME_LOOKBACK_RANGES_TO_DAYS_AGO,
  TimeLookbackRange,
} from "@types";

const displayTimeLookbackRange = (tlb: TimeLookbackRange) => {
  if (tlb === "All time") {
    return tlb;
  }
  return `Last ${tlb}`;
};

const tlrFromText = (text: string) => {
  if (text === "All time") {
    return text;
  }
  return text.replace("Last ", "");
};

export const OverviewTimeSelector: React.FC<{
  hasTeamsTier: boolean;
  showUpsell?: boolean;
}> = ({ hasTeamsTier, showUpsell = false }) => {
  const { tlr, setTlr } = useTimeLookbackRange();
  const lookbackRanges = !hasTeamsTier
    ? TIME_LOOKBACK_RANGES.filter(
        (timeRange: TimeLookbackRange) =>
          TIME_LOOKBACK_RANGES_TO_DAYS_AGO[timeRange] <= 30
      )
    : TIME_LOOKBACK_RANGES;

  useEffect(() => {
    if (!hasTeamsTier && ["All time", "Last 1 year"].indexOf(tlr) !== -1) {
      setTlr("Last 1 month");
    }
  }, [hasTeamsTier, tlr, setTlr]);

  if (showUpsell) {
    return (
      <HoverCard
        withArrow
        shadow="sm"
        width={200}
        position="bottom-end"
        arrowOffset={40}
      >
        <HoverCard.Dropdown p="md">
          <AddYourCodeUpsell incentive="filter by time" />
        </HoverCard.Dropdown>
        <HoverCard.Target>
          <div>
            <Button
              leftIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
              data-testid="TimeSelectorButton"
              disabled
              compact
            >
              <span data-testid="TimeSelectorButtonText">
                {displayTimeLookbackRange(tlr)}
              </span>
            </Button>
          </div>
        </HoverCard.Target>
      </HoverCard>
    );
  }

  return (
    <CompactSingleSelect
      options={TIME_LOOKBACK_RANGES.map((tlrItem) =>
        displayTimeLookbackRange(tlrItem)
      )}
      selectedItem={displayTimeLookbackRange(tlr)}
      onChange={(tlrText) => setTlr(tlrFromText(tlrText))}
      isItemLocked={(tlrText) => !lookbackRanges.includes(tlrFromText(tlrText))}
      data-testid="overview-time-selector"
      menuProps={{ position: "bottom-end" }}
    >
      <Button
        leftIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
        data-testid="TimeSelectorButton"
        compact
      >
        <span data-testid="TimeSelectorButtonText">
          {displayTimeLookbackRange(tlr)}
        </span>
      </Button>
    </CompactSingleSelect>
  );
};
